import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';

const PAGE_SIZE = 50;

const KINDS = [
  { value: '', label: 'All kinds' },
  { value: 'answered_drafts_unsubmitted', label: 'Answered drafts unsubmitted' },
] as const;

/**
 * Test sessions flagged with one or more self-describing issues
 * (`QSetSession.issues[]`). Sourced directly from the permanent test-sessions
 * container — durable + per-incident, not a TTL-limited log.
 */
export function SessionIssues() {
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['sessionIssues', page, kind],
    queryFn: () => adminApi.getSessionIssues({ page, pageSize: PAGE_SIZE, kind: kind || undefined }),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Session Issues</h2>
        <p className="section-description">
          Test/practice sessions that recorded an operational issue (e.g. answered
          questions that never got submitted). Read from the test-sessions records
          themselves — durable and per-incident.
        </p>
      </div>

      <div className="error-stats-controls">
        <select
          value={kind}
          onChange={e => { setKind(e.target.value); setPage(1); }}
        >
          {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
      </div>

      {isLoading && <p>Loading…</p>}
      {error && <p className="error-text">Failed to load session issues.</p>}

      {data && (
        <>
          <p className="error-stats-total">
            <strong>{data.total.toLocaleString()}</strong> session(s) with issues
          </p>

          {data.items.length === 0 ? (
            <p className="error-stats-empty">No sessions with issues. 🎉</p>
          ) : (
            <table className="error-stats-table">
              <thead>
                <tr>
                  <th>Student</th><th>Session</th><th>Status</th><th>Ended</th><th>Issues</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(s => (
                  <tr key={s.sessionId}>
                    <td>{s.user?.email ?? s.userId}</td>
                    <td>{s.questionSetTitle ?? '—'}</td>
                    <td>{s.status}</td>
                    <td>{s.endedAt ? new Date(s.endedAt).toLocaleString() : '—'}</td>
                    <td>
                      {(s.issues ?? []).map((i, idx) => (
                        <div key={idx}>
                          <code>{i.kind}</code>
                          {typeof i.detail?.unsubmittedAnsweredCount === 'number' && (
                            <> — {i.detail.unsubmittedAnsweredCount as number} unsubmitted</>
                          )}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {totalPages > 1 && (
            <div className="error-stats-controls" style={{ marginTop: 12 }}>
              <button className="window-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <span style={{ alignSelf: 'center' }}>Page {page} / {totalPages}</span>
              <button className="window-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
